import { useState, useMemo, useCallback } from 'react';
import { Flex, Drawer, Pagination } from 'antd';
import { FREE_ASSETS } from '../web2-asset-store/items/free-assets';
import { CATEGORIES, CategoryType, Item, WorldsDrawerProps } from './types';
import Cards from './components/Cards';
import DrawerTitle from './components/DrawerTitle';
import styles from './worlds-drawer.module.css';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { CDN } from '@src/components/scene/assets/baseUrl';

const PAGE_LIMIT = 18;
const blankSceneTemplateURL = `${CDN}/studioAssets/imageAssets/noSceneT.jpg`;

const BLANK_TEMPLATE = {
  id: '',
  name: 'No Scene Template',
  url: '',
  thumbnail: blankSceneTemplateURL,
  modelType: 'scene',
};

function paginate(array: Item[], currentPage: number): Item[] {
  return array.slice(currentPage * PAGE_LIMIT, (currentPage + 1) * PAGE_LIMIT);
}

export default function WorldsDrawer({
  isOpen,
  setIsOpen,
  loadScene,
  worlds,
  selectedWorld,
}: WorldsDrawerProps) {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentLength, setCurrentLength] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<CategoryType>(
    CATEGORIES.WORLDS
  );

  const { items, paginatedItems } = useMemo(() => {
    const currentItems =
      activeCategory === CATEGORIES.WORLDS
        ? [BLANK_TEMPLATE, ...(worlds || [])]
        : FREE_ASSETS.prefab || [];
    setCurrentLength(currentItems.length);
    return {
      items: currentItems,
      paginatedItems: paginate(currentItems, currentPage),
    };
  }, [activeCategory, worlds, currentPage]);

  const onLoadWorld = useCallback(
    (url: string, name: string, modelType: string, sceneThumbnail: string) => {
      if (activeCategory === CATEGORIES.WORLDS) {
        loadScene(name, url, SIDE_MENU_EVENTS.SCENES, sceneThumbnail);
      } else {
        if (modelType === 'SCENE') {
          loadScene(name, url, SIDE_MENU_EVENTS.SCENES, sceneThumbnail);
        } else {
          loadScene(name, url, SIDE_MENU_EVENTS.SCENES, sceneThumbnail);
        }
      }
      setIsOpen(false);
    },
    [loadScene, setIsOpen, activeCategory]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  return (
    <Drawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      closeIcon={false}
      footer={false}
      width='90%'
      destroyOnClose
      zIndex={1001}
      className={styles.assetsDrawer}
      title={
        <DrawerTitle
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      }
    >
      <Cards
        items={paginatedItems}
        onLoadWorld={onLoadWorld}
        selectedWorld={selectedWorld}
        activeCategory={activeCategory}
      />
      <Flex justify='center'>
        <Pagination
          defaultCurrent={currentPage + 1}
          pageSize={PAGE_LIMIT}
          total={currentLength}
          hideOnSinglePage
          showSizeChanger={false}
          onChange={handlePageChange}
          className={styles.pagination}
        />
      </Flex>
    </Drawer>
  );
}
